/**
 * Alpine.js directive for filling available vertical space with text.
 * Binary search finds the largest font-size that fits both the
 * available height (parent minus siblings) and width (parent content box).
 *
 * Usage: x-textfill="{ min: 40, max: 120 }"
 *        x-textfill="{ min: 40, max: 120, enableAbove: 1024 }"
 *
 * Config:
 *   min         — minimum font-size in px (default: 16)
 *   max         — maximum font-size in px (default: 120)
 *   enableAbove — viewport width in px above which textfill is active (default: 0 = always)
 *                 Below this breakpoint, inline font-size is removed so CSS fallback applies.
 */
export default function (Alpine) {
  Alpine.directive('textfill', (el, { expression }, { evaluate, cleanup }) => {
    const config = expression ? evaluate(expression) : {};
    const min = config.min || 16;
    const max = config.max || 120;
    const enableAbove = config.enableAbove || 0;

    let rafId = null;
    let isFitting = false;
    let isDestroyed = false;

    const measureEl = document.createElement('span');
    measureEl.style.position = 'absolute';
    measureEl.style.visibility = 'hidden';
    measureEl.style.whiteSpace = 'nowrap';
    measureEl.style.left = '-9999px';
    measureEl.style.top = '-9999px';
    measureEl.style.pointerEvents = 'none';
    (document.body || document.documentElement).appendChild(measureEl);

    function syncMeasureStyles() {
      const style = getComputedStyle(el);
      measureEl.style.fontFamily = style.fontFamily;
      measureEl.style.fontStyle = style.fontStyle;
      measureEl.style.fontWeight = style.fontWeight;
      measureEl.style.letterSpacing = style.letterSpacing;
      measureEl.style.textTransform = style.textTransform;
    }

    function getWords() {
      return el.textContent.trim().split(/\s+/).filter(Boolean);
    }

    function getMaxWordWidth(words, fontSize) {
      if (!words.length) return 0;

      measureEl.style.fontSize = `${fontSize}px`;
      let maxWidth = 0;
      for (const word of words) {
        measureEl.textContent = word;
        const width = measureEl.getBoundingClientRect().width;
        if (width > maxWidth) maxWidth = width;
      }

      return maxWidth;
    }

    /**
     * Calculate available vertical space by subtracting parent padding,
     * sibling heights, and flex gaps from the parent's client height.
     */
    function getAvailableHeight() {
      const parent = el.parentElement;
      const style = getComputedStyle(parent);
      const paddingTop = parseFloat(style.paddingTop) || 0;
      const paddingBottom = parseFloat(style.paddingBottom) || 0;
      const gap = parseFloat(style.rowGap) || parseFloat(style.gap) || 0;

      let siblingsHeight = 0;
      let siblingCount = 0;
      for (const child of parent.children) {
        if (child !== el) {
          siblingsHeight += child.offsetHeight;
          siblingCount++;
        }
      }

      const totalGap = siblingCount > 0 ? gap * siblingCount : 0;

      return parent.clientHeight - paddingTop - paddingBottom - siblingsHeight - totalGap;
    }

    /**
     * Calculate available horizontal space from the parent's content box.
     * Measured from the parent rather than the element itself because
     * flex items without explicit width shrink-wrap to content, making
     * el.clientWidth grow with font-size and defeating the overflow check.
     */
    function getAvailableWidth() {
      const parent = el.parentElement;
      const style = getComputedStyle(parent);
      const paddingLeft = parseFloat(style.paddingLeft) || 0;
      const paddingRight = parseFloat(style.paddingRight) || 0;

      // Cap to viewport to avoid min-content expansion from oversized text.
      const parentWidth = parent.clientWidth - paddingLeft - paddingRight;
      const viewportWidth = window.innerWidth - paddingLeft - paddingRight;

      return Math.min(parentWidth, viewportWidth);
    }

    /**
     * Binary search for the largest font-size where:
     * - el.scrollHeight <= available vertical space (no vertical overflow)
     * - el.scrollWidth <= parent content width (no horizontal overflow)
     *
     * When the parent auto-sizes to content (no external height constraint),
     * height checking is skipped and only width fitting is performed.
     * The element width is pinned during search to prevent ancestor expansion.
     */
    function fit() {
      if (isFitting || isDestroyed) return;

      // Breakpoint check — if below threshold, disable and use CSS fallback
      if (enableAbove > 0 && window.innerWidth < enableAbove) {
        el.style.removeProperty('font-size');
        return;
      }

      isFitting = true;

      try {
        // Reset to min before measuring to break circular dependency:
        // large font → wide min-content → ancestor expands → large availableWidth → large font.
        // At min, content fits naturally so ancestors collapse to their grid/flex-allocated width.
        el.style.fontSize = `${min}px`;

        const availableHeight = getAvailableHeight();
        const availableWidth = getAvailableWidth();
        if (availableWidth <= 0) return;

        // Detect auto-height: if even min overflows height, the parent auto-sizes
        // to content (no external height constraint). Use width-only fitting.
        const useHeightCheck = availableHeight > 0 && el.scrollHeight <= availableHeight;

        const words = getWords();
        syncMeasureStyles();

        // Pin width during binary search to prevent ancestor expansion
        // from inflating availableWidth at larger font sizes.
        const origWidth = el.style.width;
        el.style.width = `${availableWidth}px`;

        let lo = min;
        let hi = max;

        while (hi - lo > 1) {
          const mid = Math.floor((lo + hi) / 2);
          el.style.fontSize = `${mid}px`;
          const maxWordWidth = getMaxWordWidth(words, mid);

          if (
            (!useHeightCheck || el.scrollHeight <= availableHeight) &&
            maxWordWidth <= availableWidth
          ) {
            lo = mid;
          } else {
            hi = mid;
          }
        }

        // Restore original width before setting final font-size
        el.style.width = origWidth;

        // Verify lo fits — if even min overflows, remove the inline
        // style so the CSS fallback class (nf-text-112 / nf-text-112-cq) applies.
        el.style.fontSize = `${lo}px`;
        const maxWordWidth = getMaxWordWidth(words, lo);
        if (
          (useHeightCheck && el.scrollHeight > availableHeight) ||
          maxWordWidth > availableWidth
        ) {
          el.style.fontSize = '';
        }
      } finally {
        isFitting = false;
      }
    }

    function scheduleFit() {
      if (rafId || isDestroyed) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;
        fit();
      });
    }

    const observer = new ResizeObserver(scheduleFit);
    observer.observe(el.parentElement);

    let resizeTimeout;
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(fit, 100);
    };
    window.addEventListener('resize', handleResize);

    fit();
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(scheduleFit);
    }

    cleanup(() => {
      isDestroyed = true;
      observer.disconnect();
      window.removeEventListener('resize', handleResize);
      clearTimeout(resizeTimeout);
      if (rafId) cancelAnimationFrame(rafId);
      measureEl.remove();
    });
  });
}
